/**
 * Cockpit (driver's seat) rendering.
 * Pseudo-3D perspective: for each world entity, compute forward/lateral
 * distance relative to the player car, then project to screen with
 * proportional scaling. Painter's algorithm by depth.
 *
 * The dashboard chrome at the bottom of the screen is the user-supplied
 * pixel-art "inside view" image for the active car. Each car has a unique
 * dashboard, steering wheel and chrome trim — Knight has black KITT vibes,
 * Tank has battle-station controls, Toonster has googly eyes peeking up, etc.
 */
import { OUTLINE, roundRect, drawIconGlyph, visualFor } from "./itemVisuals";
import { drawCityHorizon } from "./cityScenes";
import { tickCockpitTraffic, getCockpitTraffic, drawTrafficCar } from "./cockpitTraffic";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const WORLD_W = 1280;
const WORLD_H = 820;
const HORIZON_Y = 280;
const FOCAL = 280;

// Lazy cache of dashboard images per car kind.
const _dashCache = {};
function getDashImage(kind) {
  if (_dashCache[kind] !== undefined) return _dashCache[kind];
  const img = new Image();
  img.onload = () => { _dashCache[kind] = img; };
  img.onerror = () => { _dashCache[kind] = null; };
  img.src = `${BACKEND_URL}/api/static/sprites/car_${kind}_dash.png?v=5`;
  _dashCache[kind] = "loading";
  return "loading";
}

/**
 * Project world (wx, wy) to cockpit screen coords relative to the player.
 * Returns null if behind the camera.
 */
export function project(car, wx, wy) {
  const dx = wx - car.x;
  const dy = wy - car.y;
  // Rotate into car's local frame: +x = forward, +y = right
  const c = Math.cos(-car.angle);
  const s = Math.sin(-car.angle);
  const lx = dx * c - dy * s; // forward
  const ly = dx * s + dy * c; // lateral
  if (lx < 40) return null;   // too close / behind
  const scale = FOCAL / lx;
  const sx = WORLD_W / 2 + ly * scale * 1.4;
  const sy = HORIZON_Y + (WORLD_H - HORIZON_Y - 160) * (1 - Math.min(1, 40 / lx));
  return { x: sx, y: sy, scale, depth: lx };
}

export function renderCockpit(ctx, st, remote, car, theme, pedestrians, _skylineImg) {
  // Sky
  const sky = ctx.createLinearGradient(0, 0, 0, HORIZON_Y);
  sky.addColorStop(0, theme.bg_top);
  sky.addColorStop(1, theme.bg_bottom);
  ctx.fillStyle = sky;
  ctx.fillRect(0, 0, WORLD_W, HORIZON_Y);

  // distant horizon haze
  ctx.fillStyle = "rgba(255,255,255,0.35)";
  ctx.fillRect(0, HORIZON_Y - 18, WORLD_W, 18);

  // Horizon silhouette (distant city skyline)
  drawHorizonSkyline(ctx, theme);

  // Per-city landmark silhouettes on the horizon (Eiffel for Paris,
  // Big Ben for London, pyramids for Cairo, CN Tower for Toronto, etc.)
  if (st.cityId) drawCityHorizon(ctx, st.cityId, HORIZON_Y);

  // Ground
  ctx.fillStyle = "#6DB344";
  ctx.fillRect(0, HORIZON_Y, WORLD_W, WORLD_H - HORIZON_Y);

  // Perspective road (trapezoid)
  drawPerspectiveRoad(ctx, car, theme);

  // Per-city chunky building silhouettes along both sides of the receding
  // road. Drawn AFTER the road + ground so they layer above the grass on
  // the horizon line but BELOW the projected entities (cars, items) in the
  // painter's sort below.
  if (st.cityId) drawCityRoadsideBuildings(ctx, st.cityId);

  // iter-52: spawn and update ambient NPC traffic each frame so the
  // cockpit view feels populated like a real city street, matching the
  // user's reference photos. Pure visual — no collision.
  tickCockpitTraffic(st, car);

  // Gather objects with their projection, sort far → near
  const projected = [];
  for (const o of st.obstacles) {
    const cx = o.x + o.w / 2;
    const cy = o.y + o.h / 2;
    const p = project(car, cx, cy);
    if (p) projected.push({ kind: "obstacle", data: o, proj: p });
  }
  for (const it of st.items) {
    if (it.collected) continue;
    const p = project(car, it.x, it.y);
    if (p) projected.push({ kind: "item", data: it, proj: p });
  }
  for (const e of st.enemies) {
    const p = project(car, e.x, e.y);
    if (p) projected.push({ kind: "enemy", data: e, proj: p });
  }
  for (const ped of pedestrians.list) {
    const p = project(car, ped.x, ped.y);
    if (p) projected.push({ kind: "ped", data: ped, proj: p });
  }
  for (const t of getCockpitTraffic(st)) {
    const p = project(car, t.x, t.y);
    if (p) projected.push({ kind: "traffic", data: t, proj: p });
  }
  for (const [uid, r] of Object.entries(remote)) {
    const p = project(car, r.x, r.y);
    if (p) projected.push({ kind: "remote_car", data: { ...r, uid }, proj: p });
  }
  for (const m of st.missiles) {
    const p = project(car, m.x, m.y);
    if (p) projected.push({ kind: "missile", data: m, proj: p });
  }
  for (const m of st.remoteMissiles) {
    const p = project(car, m.x, m.y);
    if (p) projected.push({ kind: "remote_missile", data: m, proj: p });
  }
  projected.sort((a, b) => b.proj.depth - a.proj.depth);

  for (const e of projected) {
    drawProjected(ctx, e);
  }

  // Mood overlay on upper half
  drawMoodOverlay(ctx, theme);

  // iter-52: windshield frame — A-pillar, headliner, and gentle vignette
  // around the screen edges to sell the "you're inside the car" feeling.
  // Drawn AFTER projected entities so it visually frames them, BEFORE the
  // dashboard chrome which has its own opaque region at the bottom.
  drawWindshieldFrame(ctx);

  // Rear-view mirror — small mirror at top-center showing the world BEHIND
  // the player. Reverses the projection so cops chasing you appear here.
  drawRearViewMirror(ctx, st, car, theme);

  // Cockpit dashboard chrome — the user's pixel-art image for this car
  // (steering wheel, gauges, hood, etc). Renders at the bottom of the canvas
  // covering ~30% of the height. Falls back to the canvas-drawn dashboard if
  // the image hasn't loaded yet.
  const dashImg = getDashImage(car.kind || "cruiser");
  if (dashImg && dashImg !== "loading" && dashImg.complete) {
    drawSpriteDashboard(ctx, dashImg, st, car);
  } else {
    drawDashboard(ctx, st, car);
    drawSteeringWheel(ctx, st);
  }

  // View indicator
  ctx.fillStyle = "rgba(255,209,102,0.9)";
  ctx.strokeStyle = OUTLINE;
  ctx.lineWidth = 2;
  ctx.beginPath();
  roundRect(ctx, 14, 14, 130, 26, 4);
  ctx.fill();
  ctx.stroke();
  ctx.fillStyle = OUTLINE;
  ctx.font = "bold 14px 'Anton'";
  ctx.textAlign = "left";
  ctx.textBaseline = "middle";
  ctx.fillText("COCKPIT · CTRL+SHIFT", 22, 28);
}

// --- Rear-view mirror -------------------------------------------------------
// A small rectangular mirror at the top-center of the cockpit showing the
// view BEHIND the player. Cops chasing get rendered here so the player
// gets the classic "they're right behind me!" tension without leaving
// cockpit view. The mirror reverses depth: behind = inside the mirror.
function drawRearViewMirror(ctx, st, car, theme) {
  const mw = 380;
  const mh = 90;
  const mx = WORLD_W / 2 - mw / 2;
  const my = 8;

  // Mirror frame (chunky black)
  ctx.fillStyle = "#0a0a0a";
  ctx.fillRect(mx - 6, my - 6, mw + 12, mh + 12);
  // Inner mirror glass background — tinted sky to suggest looking back
  const grad = ctx.createLinearGradient(0, my, 0, my + mh);
  grad.addColorStop(0, theme.bg_top);
  grad.addColorStop(0.55, theme.bg_bottom);
  grad.addColorStop(1, "#3D5A2D"); // grass behind
  ctx.fillStyle = grad;
  ctx.fillRect(mx, my, mw, mh);
  // Clip everything to the mirror
  ctx.save();
  ctx.beginPath();
  ctx.rect(mx, my, mw, mh);
  ctx.clip();

  // Receding road, reversed (foreground at top — narrow, far at bottom — wide).
  // Actually for a rear-view we want: car at the BOTTOM of the mirror (closest
  // to player), road receding UPWARD into the mirror's distance.
  ctx.fillStyle = theme.road;
  const cx = mx + mw / 2;
  ctx.beginPath();
  ctx.moveTo(cx - 12, my);
  ctx.lineTo(cx + 12, my);
  ctx.lineTo(mx + mw, my + mh);
  ctx.lineTo(mx, my + mh);
  ctx.closePath();
  ctx.fill();
  // Center stripe scrolling toward player (reverse of front view)
  const t = (performance.now() / 200) % 1;
  ctx.fillStyle = theme.stripe;
  for (let i = 0; i < 5; i++) {
    const f0 = (i + t) / 5;
    const f1 = (i + 0.5 + t) / 5;
    if (f1 > 1) continue;
    const y0 = my + mh * f0;
    const y1 = my + mh * f1;
    const half0 = 1 + (mw / 2 - 1) * f0;
    const half1 = 1 + (mw / 2 - 1) * f1;
    ctx.beginPath();
    ctx.moveTo(cx - half0 * 0.04, y0);
    ctx.lineTo(cx + half0 * 0.04, y0);
    ctx.lineTo(cx + half1 * 0.04, y1);
    ctx.lineTo(cx - half1 * 0.04, y1);
    ctx.closePath();
    ctx.fill();
  }

  // Project enemies (and remote players) BEHIND the player into the mirror.
  // We swap the angle by π so the projection sees what's behind.
  const fakeCar = { x: car.x, y: car.y, angle: car.angle + Math.PI };
  const projInMirror = (wx, wy) => {
    const dx = wx - fakeCar.x, dy = wy - fakeCar.y;
    const c = Math.cos(-fakeCar.angle);
    const s = Math.sin(-fakeCar.angle);
    const lx = dx * c - dy * s;
    const ly = dx * s + dy * c;
    if (lx < 30) return null;
    const scale = 80 / lx;
    return {
      sx: cx + ly * scale * 1.6,
      sy: my + mh - mh * Math.min(1, 30 / lx),
      scale: Math.max(0.25, Math.min(1.2, scale * 4)),
    };
  };

  for (const e of st.enemies) {
    if (e.disabled > performance.now()) continue;
    const p = projInMirror(e.x, e.y);
    if (!p || p.sx < mx || p.sx > mx + mw) continue;
    const w = 24 * p.scale;
    const h = 14 * p.scale;
    // Body — cop white-and-black, FBI all black
    ctx.fillStyle = e.type === "fbi" ? "#181818" : "#FFFFFF";
    ctx.fillRect(p.sx - w / 2, p.sy - h, w, h);
    if (e.type === "cop") {
      ctx.fillStyle = "#0e0c12";
      ctx.fillRect(p.sx - w / 2, p.sy - h * 0.6, w, h * 0.4);
    }
    // Lightbar
    ctx.fillStyle = (Math.floor(performance.now() / 180) % 2) ? "#FF3030" : "#1E90FF";
    ctx.fillRect(p.sx - w * 0.4, p.sy - h - 2, w * 0.8, 2);
  }

  ctx.restore();
  // Mirror outer rim highlight
  ctx.strokeStyle = "#22202a";
  ctx.lineWidth = 2;
  ctx.strokeRect(mx, my, mw, mh);
  // "REAR VIEW" tiny tag
  ctx.fillStyle = "rgba(255,255,255,0.6)";
  ctx.font = "bold 9px 'Press Start 2P', monospace";
  ctx.textAlign = "right";
  ctx.fillText("REAR VIEW", mx + mw - 6, my + mh - 6);
}

// --- Per-city cockpit roadside buildings ------------------------------------
// Chunky pixel-art building silhouettes placed on BOTH sides of the
// receding cockpit road. Each city has a distinct palette + proportions.
// Buildings appear in the receding perspective so they shrink as they
// approach the horizon.
const CITY_BUILDING_PALETTES = {
  paris: { walls: ["#E8D5B0", "#D2BA8B", "#BFA372"], roofs: "#3E4449", windows: "#FFE499" },
  london: { walls: ["#A64E4E", "#8B3535", "#6F2828"], roofs: "#2F2F2F", windows: "#F5E3B0" },
  tokyo: { walls: ["#1A1F3A", "#252B4D", "#3D2A5C"], roofs: "#1A1A1A", windows: "#FF3DF8" },
  nyc: { walls: ["#8B7355", "#A9896F", "#6B5E4B"], roofs: "#2F2F2F", windows: "#FFD700" },
  la: { walls: ["#F5D5A8", "#E8B580", "#D99066"], roofs: "#7A4A2F", windows: "#FFFFFF" },
  mexico_city: { walls: ["#F2A0C8", "#FFD23F", "#7ED6A5"], roofs: "#8B4513", windows: "#FFFFFF" },
  cairo: { walls: ["#E8C987", "#C9A86B", "#D4B77A"], roofs: "#A0845A", windows: "#FFD580" },
  toronto: { walls: ["#C9D1D9", "#8DA5B8", "#5A7A95"], roofs: "#2F3F4F", windows: "#FFFFFF" },
  beirut: { walls: ["#F5E8D0", "#E8D5B5", "#D4C195"], roofs: "#4A90C8", windows: "#5E9FE8" },
  detroit: { walls: ["#8B4513", "#5D2F0A", "#B87333"], roofs: "#2F2F2F", windows: "#FFB347" },
};

export function drawCityRoadsideBuildings(ctx, cityId) {
  const pal = CITY_BUILDING_PALETTES[cityId];
  if (!pal) return;
  const groundH = WORLD_H - HORIZON_Y;
  // Two arrays of building slots — one on each side. Each slot has a
  // stable t (depth 0..1) and a deterministic seed.
  const SLOTS = 7;
  for (let side = -1; side <= 1; side += 2) {
    for (let i = 0; i < SLOTS; i++) {
      const t = Math.pow((i + 0.5) / SLOTS, 1.5);
      if (t < 0.10) continue;
      const seed = (i * 2153 + (side === -1 ? 17 : 131)) % 409;
      const y = HORIZON_Y + groundH * t;
      // Road half-width at this depth
      const halfT = 30 + (WORLD_W / 2 - 30) * t;
      // Building spawn x: ~50-120 outside the road edge
      const outside = 46 + 120 * t;
      const bx = WORLD_W / 2 + side * (halfT + outside);
      // Sizes (chunky)
      const bw = 40 + (seed % 36) + 120 * t;
      const bh = 60 + ((seed * 7) % 70) + 120 * t;
      const by = y - bh;
      // Don't draw if entirely above the horizon strip
      if (by > HORIZON_Y - 20) continue;
      // Wall
      const wallColor = pal.walls[seed % pal.walls.length];
      ctx.fillStyle = wallColor;
      ctx.fillRect(bx - bw / 2, by, bw, bh);
      // Roof (slightly darker block or slanted for Paris)
      if (cityId === "paris") {
        ctx.fillStyle = pal.roofs;
        ctx.beginPath();
        ctx.moveTo(bx - bw / 2 - 2, by);
        ctx.lineTo(bx, by - Math.max(6, bw * 0.12));
        ctx.lineTo(bx + bw / 2 + 2, by);
        ctx.closePath();
        ctx.fill();
      } else {
        ctx.fillStyle = pal.roofs;
        ctx.fillRect(bx - bw / 2 - 2, by - 4, bw + 4, 4);
      }
      // Window grid (chunky)
      ctx.fillStyle = pal.windows;
      const cols = Math.max(2, Math.floor(bw / 14));
      const rows = Math.max(3, Math.floor(bh / 14));
      for (let c = 0; c < cols; c++) {
        for (let r = 0; r < rows; r++) {
          if ((c * 3 + r * 5 + seed) % 4 === 0) continue;
          const wx = bx - bw / 2 + 4 + c * ((bw - 8) / Math.max(1, cols - 1)) - 1;
          const wy = by + 5 + r * ((bh - 10) / Math.max(1, rows - 1)) - 1;
          ctx.fillRect(wx, wy, 3, 3);
        }
      }
      // Outline
      ctx.strokeStyle = OUTLINE;
      ctx.lineWidth = Math.max(1, 2 * t);
      ctx.strokeRect(bx - bw / 2, by, bw, bh);
    }
  }
}

function drawHorizonSkyline(ctx, theme) {
  ctx.save();
  ctx.lineJoin = "round";
  // colourful pop-art skyline — same vibe as the map view skybar
  const palette = ["#FF3D7F", "#7B2CBF", "#FFC857", "#3A86FF", "#06D6A0", "#F26419", "#118AB2", "#EF476F"];
  let x = 0;
  let i = 0;
  while (x < WORLD_W) {
    const seed = (i * 9301 + 49297) % 233280;
    const w = 28 + ((seed >> 4) % 60);
    const h = 40 + ((seed >> 2) % 90);
    ctx.fillStyle = palette[i % palette.length];
    ctx.fillRect(x, HORIZON_Y - h, w, h);
    ctx.strokeStyle = OUTLINE;
    ctx.lineWidth = 2;
    ctx.strokeRect(x, HORIZON_Y - h, w, h);
    // simple window dots
    ctx.fillStyle = "#FFFFFF";
    for (let yy = HORIZON_Y - h + 6; yy < HORIZON_Y - 6; yy += 8) {
      for (let xx = x + 4; xx < x + w - 4; xx += 8) {
        if (((xx + yy + i) % 17) === 0) continue;
        ctx.fillRect(xx, yy, 3, 3);
      }
    }
    x += w + 2;
    i++;
  }
  ctx.strokeStyle = OUTLINE;
  ctx.lineWidth = 3;
  ctx.beginPath();
  ctx.moveTo(0, HORIZON_Y);
  ctx.lineTo(WORLD_W, HORIZON_Y);
  ctx.stroke();
  ctx.restore();
}

function drawPerspectiveRoad(ctx, car, theme) {
  ctx.save();
  const speed = Math.hypot(car.vx, car.vy);
  const scrollSpeed = 20 + speed * 30;
  const scroll = (performance.now() / (200 - Math.min(150, scrollSpeed * 2))) % 1;
  const groundH = WORLD_H - HORIZON_Y;

  // === Main road asphalt (trapezoid) ===
  ctx.fillStyle = theme.road;
  ctx.beginPath();
  ctx.moveTo(WORLD_W / 2 - 30, HORIZON_Y);
  ctx.lineTo(WORLD_W / 2 + 30, HORIZON_Y);
  ctx.lineTo(WORLD_W, WORLD_H);
  ctx.lineTo(0, WORLD_H);
  ctx.closePath();
  ctx.fill();

  // === Pole Position red/white curb stripes receding along both rails ===
  // Drawn as alternating bands on the very EDGE of the road trapezoid.
  // Using a parametric sweep from t=0 (horizon, narrow) to t=1 (foreground).
  const strips = 18;
  for (let i = 0; i < strips; i++) {
    const t0 = Math.pow((i + scroll) / strips, 1.8);
    const t1 = Math.pow((i + 1 + scroll) / strips, 1.8);
    if (t0 > 1) continue;
    const y0 = HORIZON_Y + groundH * t0;
    const y1 = HORIZON_Y + groundH * Math.min(1, t1);
    // Road edges at each y: linear interp from ±30 at horizon to ±WORLD_W/2 at ground
    const halfT0 = 30 + (WORLD_W / 2 - 30) * t0;
    const halfT1 = 30 + (WORLD_W / 2 - 30) * Math.min(1, t1);
    const curbW0 = Math.max(2, 18 * t0);
    const curbW1 = Math.max(3, 18 * Math.min(1, t1));
    const color = (i % 2 === 0) ? "#E63946" : "#FFFFFF";
    ctx.fillStyle = color;
    // LEFT curb strip: inside the road, right up against the left edge
    ctx.beginPath();
    ctx.moveTo(WORLD_W / 2 - halfT0, y0);
    ctx.lineTo(WORLD_W / 2 - halfT0 + curbW0, y0);
    ctx.lineTo(WORLD_W / 2 - halfT1 + curbW1, y1);
    ctx.lineTo(WORLD_W / 2 - halfT1, y1);
    ctx.closePath();
    ctx.fill();
    // RIGHT curb strip
    ctx.beginPath();
    ctx.moveTo(WORLD_W / 2 + halfT0, y0);
    ctx.lineTo(WORLD_W / 2 + halfT0 - curbW0, y0);
    ctx.lineTo(WORLD_W / 2 + halfT1 - curbW1, y1);
    ctx.lineTo(WORLD_W / 2 + halfT1, y1);
    ctx.closePath();
    ctx.fill();
  }

  // Outline around the road
  ctx.strokeStyle = OUTLINE;
  ctx.lineWidth = 4;
  ctx.beginPath();
  ctx.moveTo(WORLD_W / 2 - 30, HORIZON_Y);
  ctx.lineTo(WORLD_W / 2 + 30, HORIZON_Y);
  ctx.lineTo(WORLD_W, WORLD_H);
  ctx.lineTo(0, WORLD_H);
  ctx.closePath();
  ctx.stroke();

  // === Center dashed yellow stripes (scrolls with speed) ===
  ctx.fillStyle = theme.stripe;
  ctx.strokeStyle = OUTLINE;
  ctx.lineWidth = 2;
  const segs = 10;
  for (let i = 0; i < segs; i++) {
    const t0 = (i + scroll) / segs;
    const t1 = (i + 0.45 + scroll) / segs;
    if (t0 > 1) continue;
    const x0 = WORLD_W / 2;
    const y0 = HORIZON_Y + groundH * t0;
    const y1 = HORIZON_Y + groundH * Math.min(1, t1);
    const w0 = 4 + 22 * t0;
    const w1 = 4 + 22 * Math.min(1, t1);
    ctx.beginPath();
    ctx.moveTo(x0 - w0, y0);
    ctx.lineTo(x0 + w0, y0);
    ctx.lineTo(x0 + w1, y1);
    ctx.lineTo(x0 - w1, y1);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
  }

  // === Trackside telephone poles (Speed Racer / Night Driver) ===
  const poleSlots = 9;
  for (let i = 0; i < poleSlots; i++) {
    const t = ((i + scroll * 1.2) % poleSlots) / poleSlots;
    if (t < 0.02) continue;
    const y = HORIZON_Y + groundH * Math.pow(t, 1.4);
    const poleH = 6 + 110 * Math.pow(t, 1.3);
    const side = (i % 2 === 0) ? -1 : 1;
    const trackHalf = 30 + (WORLD_W / 2 - 30) * Math.pow(t, 2);
    const offset = 16 + 50 * Math.pow(t, 1.5);
    const x = WORLD_W / 2 + side * (trackHalf + offset);
    const poleW = Math.max(2, 5 * t);
    ctx.fillStyle = "#3A2A1A";
    ctx.fillRect(x - poleW / 2, y - poleH, poleW, poleH);
    const armW = 10 + 34 * t;
    const armH = Math.max(1.5, 3 * t);
    ctx.fillRect(x - armW / 2, y - poleH + poleH * 0.1, armW, armH);
    ctx.fillStyle = "rgba(255,255,255,0.18)";
    ctx.fillRect(x - poleW / 2, y - poleH, Math.max(1, poleW / 3), poleH);
  }

  // === Chevron road arrows scrolling toward the camera (Top Gear warning signs) ===
  // Drawn centered on the road, growing as they approach.
  const chev = 5;
  for (let i = 0; i < chev; i++) {
    const ct = ((i + scroll * 0.5) % chev) / chev;
    if (ct < 0.15 || ct > 0.97) continue;
    const cy = HORIZON_Y + groundH * Math.pow(ct, 1.6);
    const cw = 16 + 90 * Math.pow(ct, 1.7);
    const cl = 6 + 34 * Math.pow(ct, 1.7);
    // double-chevron: draw two arrows stacked
    for (let j = 0; j < 2; j++) {
      const yy = cy - j * cl * 0.55;
      ctx.fillStyle = "#FFCC00";
      ctx.strokeStyle = OUTLINE;
      ctx.lineWidth = Math.max(1.5, 2 * ct);
      ctx.beginPath();
      ctx.moveTo(WORLD_W / 2 - cw / 2, yy);
      ctx.lineTo(WORLD_W / 2, yy - cl);
      ctx.lineTo(WORLD_W / 2 + cw / 2, yy);
      ctx.lineTo(WORLD_W / 2 + cw / 3, yy);
      ctx.lineTo(WORLD_W / 2, yy - cl * 0.6);
      ctx.lineTo(WORLD_W / 2 - cw / 3, yy);
      ctx.closePath();
      ctx.fill();
      ctx.stroke();
    }
  }
  ctx.restore();
}

function drawMoodOverlay(ctx, theme) {
  if (theme.mood === "rain") {
    ctx.save();
    ctx.strokeStyle = "rgba(60, 100, 140, 0.45)";
    ctx.lineWidth = 1.5;
    const t = performance.now() / 18;
    for (let i = 0; i < 120; i++) {
      const x = (i * 71 + t) % WORLD_W;
      const y = (i * 37 + t * 4) % HORIZON_Y;
      ctx.beginPath();
      ctx.moveTo(x, y);
      ctx.lineTo(x + 3, y + 14);
      ctx.stroke();
    }
    ctx.restore();
  } else if (theme.mood === "snow") {
    ctx.save();
    ctx.fillStyle = "#FFFFFF";
    const t = performance.now() / 40;
    for (let i = 0; i < 60; i++) {
      const x = (i * 93 + t * 0.7) % WORLD_W;
      const y = (i * 53 + t) % HORIZON_Y;
      ctx.beginPath();
      ctx.arc(x, y, 2.5, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.restore();
  } else if (theme.mood === "neon") {
    ctx.save();
    ctx.globalAlpha = 0.2;
    ctx.fillStyle = theme.accent_glow;
    for (let i = 0; i < 4; i++) {
      const y = (performance.now() / 40 + i * 120) % HORIZON_Y;
      ctx.fillRect(0, y, WORLD_W, 3);
    }
    ctx.restore();
  } else if (theme.mood === "fog") {
    ctx.fillStyle = "rgba(240,242,245,0.35)";
    ctx.fillRect(0, 0, WORLD_W, WORLD_H * 0.6);
  } else if (theme.mood === "desert") {
    ctx.fillStyle = "rgba(230,180,90,0.22)";
    ctx.fillRect(0, 0, WORLD_W, WORLD_H);
  }
}

function drawProjected(ctx, e) {
  const { kind, data, proj } = e;
  ctx.save();
  ctx.lineJoin = "round";
  ctx.strokeStyle = OUTLINE;

  if (kind === "traffic") {
    // Ambient NPC car — pure visual decoration. No alt logic, no
    // collision. Drawn before the obstacle/item/cop branches so the
    // depth-sort still places it correctly via the painter's algo.
    const adapted = { px: proj.x, py: proj.y, scale: proj.scale };
    drawTrafficCar(ctx, adapted, data);
    ctx.restore();
    return;
  }

  if (kind === "obstacle") {
    const size = 120 * proj.scale;
    if (data.type === "construction") {
      ctx.fillStyle = "#FFA300";
      ctx.fillRect(proj.x - size / 2, proj.y - size / 3, size, size / 2);
      ctx.lineWidth = Math.max(2, 4 * proj.scale);
      ctx.strokeRect(proj.x - size / 2, proj.y - size / 3, size, size / 2);
      ctx.fillStyle = OUTLINE;
      for (let i = 0; i < size; i += 16 * proj.scale) {
        ctx.fillRect(proj.x - size / 2 + i, proj.y - size / 3, 8 * proj.scale, size / 2);
      }
    } else if (data.type === "sewer") {
      ctx.fillStyle = "#3D3D3D";
      ctx.beginPath();
      ctx.ellipse(proj.x, proj.y, size / 2, size / 5, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.lineWidth = Math.max(2, 3 * proj.scale);
      ctx.stroke();
    } else {
      ctx.fillStyle = "#EFDAB0";
      ctx.fillRect(proj.x - size / 2, proj.y - size * 0.5, size, size * 0.5);
      ctx.lineWidth = Math.max(2, 4 * proj.scale);
      ctx.strokeRect(proj.x - size / 2, proj.y - size * 0.5, size, size * 0.5);
      ctx.fillStyle = "#D9B878";
      ctx.beginPath();
      ctx.moveTo(proj.x - size / 2 - 4, proj.y - size * 0.5);
      ctx.lineTo(proj.x, proj.y - size * 0.75);
      ctx.lineTo(proj.x + size / 2 + 4, proj.y - size * 0.5);
      ctx.closePath();
      ctx.fill();
      ctx.stroke();
    }
  } else if (kind === "item") {
    const size = 70 * proj.scale;
    const vis = visualFor(data.id, data.name);
    const pulse = 1 + 0.1 * Math.sin(performance.now() / 260);
    ctx.fillStyle = vis.color;
    ctx.lineWidth = Math.max(2, 5 * proj.scale);
    ctx.beginPath();
    ctx.arc(proj.x, proj.y - size / 2, size / 2 * pulse, 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();
    // icon
    if (proj.scale > 0.35) {
      drawIconGlyph(ctx, vis.icon, proj.x, proj.y - size / 2, Math.max(8, 20 * proj.scale));
    }
    // sign above
    if (proj.scale > 0.25) {
      ctx.save();
      ctx.font = `bold ${Math.max(11, Math.floor(20 * proj.scale))}px 'Anton'`;
      const w = ctx.measureText(vis.label).width + 16 * proj.scale;
      const h = Math.max(18, 28 * proj.scale);
      ctx.fillStyle = "#FFD166";
      ctx.lineWidth = Math.max(2, 3 * proj.scale);
      ctx.beginPath();
      roundRect(ctx, proj.x - w / 2, proj.y - size - h, w, h, 5);
      ctx.fill();
      ctx.stroke();
      ctx.fillStyle = OUTLINE;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText(vis.label, proj.x, proj.y - size - h / 2 + 1);
      ctx.restore();
    }
  } else if (kind === "enemy") {
    const scale = proj.scale;
    const w = 70 * scale, h = 50 * scale;
    ctx.fillStyle = data.type === "fbi" ? "#1a1a1a" : "#1e4d8f";
    ctx.lineWidth = Math.max(2, 4 * scale);
    ctx.beginPath();
    roundRect(ctx, proj.x - w / 2, proj.y - h, w, h, Math.max(2, 8 * scale));
    ctx.fill();
    ctx.stroke();
    // lightbar flashing
    const flash = Math.floor(performance.now() / 160) % 2 === 0;
    ctx.fillStyle = flash ? "#E63946" : "#3B82F6";
    ctx.fillRect(proj.x - w / 4, proj.y - h - 6 * scale, w / 2, 5 * scale);
    ctx.strokeRect(proj.x - w / 4, proj.y - h - 6 * scale, w / 2, 5 * scale);
    ctx.fillStyle = "#FFD700";
    ctx.font = `bold ${Math.max(10, 14 * scale)}px 'Anton'`;
    ctx.textAlign = "center";
    ctx.strokeStyle = OUTLINE;
    ctx.lineWidth = Math.max(1.5, 2.5 * scale);
    const label = data.type.toUpperCase();
    ctx.strokeText(label, proj.x, proj.y - h - 16 * scale);
    ctx.fillText(label, proj.x, proj.y - h - 16 * scale);
  } else if (kind === "ped") {
    const scale = proj.scale;
    const h = 30 * scale;
    // simple cartoon body
    ctx.fillStyle = data.shirt;
    ctx.lineWidth = Math.max(1.5, 3 * scale);
    ctx.beginPath();
    roundRect(ctx, proj.x - 8 * scale, proj.y - h, 16 * scale, h * 0.6, 3 * scale);
    ctx.fill();
    ctx.stroke();
    // head
    ctx.fillStyle = data.skin;
    ctx.beginPath();
    ctx.arc(proj.x, proj.y - h - 6 * scale, 7 * scale, 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();
    // panic "!"
    if (data.panic && proj.scale > 0.4) {
      ctx.fillStyle = "#E63946";
      ctx.font = `bold ${Math.floor(16 * scale)}px 'Anton'`;
      ctx.textAlign = "center";
      ctx.fillText("!", proj.x + 14 * scale, proj.y - h - 12 * scale);
    }
  } else if (kind === "remote_car") {
    const scale = proj.scale;
    const w = 80 * scale, h = 56 * scale;
    ctx.fillStyle = data.body_color || "#4A90E2";
    ctx.lineWidth = Math.max(2, 4 * scale);
    ctx.beginPath();
    roundRect(ctx, proj.x - w / 2, proj.y - h, w, h, Math.max(3, 8 * scale));
    ctx.fill();
    ctx.stroke();
    ctx.fillStyle = "#FFFFFF";
    ctx.font = `bold ${Math.max(10, 14 * scale)}px 'Anton'`;
    ctx.textAlign = "center";
    ctx.strokeStyle = OUTLINE;
    ctx.lineWidth = Math.max(1.5, 2.5 * scale);
    const lbl = (data.name || "RACER").toUpperCase();
    ctx.strokeText(lbl, proj.x, proj.y - h - 8 * scale);
    ctx.fillText(lbl, proj.x, proj.y - h - 8 * scale);
  } else if (kind === "missile" || kind === "remote_missile") {
    const scale = proj.scale;
    const color = kind === "missile" ? "#FFD166" : "#E63946";
    ctx.fillStyle = color;
    ctx.lineWidth = Math.max(1.5, 2.5 * scale);
    ctx.beginPath();
    ctx.arc(proj.x, proj.y - 8 * scale, Math.max(4, 10 * scale), 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();
  }

  ctx.restore();
}

// Draw the user-supplied pixel-art dashboard image at the bottom of the
// screen, then overlay a translucent classic-arcade HUD on top (MPH digital
// readout + ITEMS counter + minimap) so the player still has a clean
// data layer regardless of which car they're driving.
function drawSpriteDashboard(ctx, img, st, car) {
  ctx.save();
  ctx.imageSmoothingEnabled = false;
  // Dash height ~ 30% of canvas, anchored to bottom
  const dashH = Math.round(WORLD_H * 0.32);
  const dashY = WORLD_H - dashH;
  ctx.drawImage(img, 0, dashY, WORLD_W, dashH);

  // Translucent black strip behind HUD overlays so they're readable on any car
  ctx.fillStyle = "rgba(0,0,0,0.55)";
  ctx.fillRect(0, dashY, WORLD_W, 56);

  // MPH numeric readout (left)
  const mph = Math.min(260, Math.round(Math.hypot(car.vx, car.vy) * 30));
  ctx.fillStyle = "#FFCC00";
  ctx.font = "bold 36px 'Press Start 2P', monospace";
  ctx.textAlign = "left";
  ctx.strokeStyle = "#000";
  ctx.lineWidth = 4;
  const mphText = String(mph).padStart(3, "0");
  ctx.strokeText(mphText, 24, dashY + 42);
  ctx.fillText(mphText, 24, dashY + 42);
  ctx.fillStyle = "#FFF";
  ctx.font = "bold 14px 'Press Start 2P', monospace";
  ctx.strokeText("MPH", 200, dashY + 38);
  ctx.fillText("MPH", 200, dashY + 38);

  // Speedometer bar (left, just under the MPH text)
  drawSpeedoBar(ctx, 24, dashY + 56, 280, 18, mph);

  // ITEMS counter (center-top)
  const collected = st.items.filter((i) => i.collected).length;
  const total = st.items.length || 5;
  ctx.fillStyle = "#FFCC00";
  ctx.font = "bold 14px 'Press Start 2P', monospace";
  ctx.textAlign = "center";
  ctx.strokeText("ITEMS", WORLD_W / 2, dashY + 22);
  ctx.fillText("ITEMS", WORLD_W / 2, dashY + 22);
  ctx.fillStyle = "#FFF";
  ctx.font = "bold 22px 'Press Start 2P', monospace";
  ctx.strokeText(`${collected}/${total}`, WORLD_W / 2, dashY + 48);
  ctx.fillText(`${collected}/${total}`, WORLD_W / 2, dashY + 48);

  // POS badge (right of ITEMS)
  ctx.textAlign = "left";
  ctx.fillStyle = "#E63946";
  ctx.font = "bold 14px 'Press Start 2P', monospace";
  ctx.strokeText("POS", WORLD_W / 2 + 96, dashY + 22);
  ctx.fillText("POS", WORLD_W / 2 + 96, dashY + 22);
  ctx.fillStyle = "#FFF";
  ctx.font = "bold 22px 'Press Start 2P', monospace";
  ctx.strokeText("1ST", WORLD_W / 2 + 96, dashY + 48);
  ctx.fillText("1ST", WORLD_W / 2 + 96, dashY + 48);

  // Minimap (top-right of dashboard strip)
  drawMiniMap(ctx, st, WORLD_W - 170, dashY + 36, 260, 56);

  ctx.restore();
}

function drawDashboard(ctx, st, car) {
  ctx.save();
  const dashTop = WORLD_H - 160;
  // main dash panel
  const grad = ctx.createLinearGradient(0, dashTop, 0, WORLD_H);
  grad.addColorStop(0, "#1a1f2e");
  grad.addColorStop(1, "#0a0d14");
  ctx.fillStyle = grad;
  ctx.fillRect(0, dashTop, WORLD_W, 160);
  ctx.strokeStyle = OUTLINE;
  ctx.lineWidth = 6;
  ctx.beginPath();
  ctx.moveTo(0, dashTop);
  ctx.quadraticCurveTo(WORLD_W / 2, dashTop - 28, WORLD_W, dashTop);
  ctx.stroke();
  ctx.fillStyle = "#1a1f2e";
  ctx.beginPath();
  ctx.moveTo(0, dashTop);
  ctx.quadraticCurveTo(WORLD_W / 2, dashTop - 28, WORLD_W, dashTop);
  ctx.lineTo(WORLD_W, dashTop);
  ctx.closePath();
  ctx.fill();

  // --- LEFT: Top-Gear style speedometer BAR + big MPH readout ---
  const mph = Math.min(260, Math.round(Math.hypot(car.vx, car.vy) * 30));
  drawSpeedoBar(ctx, 40, dashTop + 24, 280, 28, mph);
  drawBigMPH(ctx, 40, dashTop + 70, mph);

  // --- CENTER: LAP / POS classic arcade indicator ---
  drawLapPos(ctx, WORLD_W / 2, dashTop + 34, st);

  // --- RIGHT: minimap ---
  drawMiniMap(ctx, st, WORLD_W - 180, dashTop + 78, 280, 110);

  // --- FAR LEFT bottom: HP bar + weapon icons ---
  drawDashHP(ctx, 40, dashTop + 124, 280, 18, st.player.hp / (st.player.max_hp || 100));
  ctx.restore();
}

// Horizontal speedometer bar green→yellow→red (Top Gear inspired)
function drawSpeedoBar(ctx, x, y, w, h, mph) {
  // Frame
  ctx.fillStyle = "#0a0d14";
  ctx.fillRect(x, y, w, h);
  ctx.strokeStyle = OUTLINE; ctx.lineWidth = 3;
  ctx.strokeRect(x, y, w, h);
  // Fill: width based on mph/260
  const pct = Math.min(1, mph / 240);
  const fillW = (w - 4) * pct;
  // Chunky pixel segments so it reads as 16-bit
  const segs = 30;
  const segW = (w - 4) / segs;
  for (let i = 0; i < segs; i++) {
    const segPct = i / segs;
    if (segPct > pct) break;
    let color;
    if (segPct < 0.55) color = "#4AE863";
    else if (segPct < 0.80) color = "#FFCC00";
    else color = "#E63946";
    ctx.fillStyle = color;
    ctx.fillRect(x + 2 + i * segW, y + 2, segW - 1, h - 4);
  }
  // Tick marks
  ctx.strokeStyle = "#000";
  ctx.lineWidth = 1;
  for (let i = 0; i <= 10; i++) {
    const tx = x + (w * i) / 10;
    ctx.beginPath();
    ctx.moveTo(tx, y + h - 4);
    ctx.lineTo(tx, y + h);
    ctx.stroke();
  }
  // Label
  ctx.fillStyle = "#FFF";
  ctx.font = "bold 10px 'Press Start 2P', monospace";
  ctx.fillText("SPEED", x, y - 6);
}

function drawBigMPH(ctx, x, y, mph) {
  // Pixel digital-style number + MPH label
  ctx.fillStyle = "#FFCC00";
  ctx.font = "bold 42px 'Press Start 2P', monospace";
  ctx.textAlign = "left";
  const text = String(mph).padStart(3, "0");
  ctx.strokeStyle = "#000"; ctx.lineWidth = 4;
  ctx.strokeText(text, x, y + 40);
  ctx.fillText(text, x, y + 40);
  ctx.fillStyle = "#FFF";
  ctx.font = "bold 16px 'Press Start 2P', monospace";
  ctx.strokeText("MPH", x + 200, y + 34);
  ctx.fillText("MPH", x + 200, y + 34);
}

function drawLapPos(ctx, cx, cy, st) {
  // Count items as "lap progress" — treat "5/5 items" like completing a lap.
  const collected = st.items.filter((i) => i.collected).length;
  const total = st.items.length || 5;
  // POS = alphabetic rank of player name, else 1
  const pos = 1;
  // LAP indicator — black panel with yellow digits
  const panelW = 120, panelH = 56;
  ctx.save();
  ctx.translate(cx - panelW / 2, cy);
  ctx.fillStyle = "#000";
  ctx.strokeStyle = "#FFCC00"; ctx.lineWidth = 3;
  ctx.fillRect(0, 0, panelW, panelH);
  ctx.strokeRect(0, 0, panelW, panelH);
  ctx.fillStyle = "#FFCC00";
  ctx.font = "bold 12px 'Press Start 2P', monospace";
  ctx.textAlign = "center";
  ctx.fillText("ITEMS", panelW / 2, 18);
  ctx.fillStyle = "#FFF";
  ctx.font = "bold 22px 'Press Start 2P', monospace";
  ctx.fillText(`${collected}/${total}`, panelW / 2, 44);
  // POS badge to the right
  ctx.translate(panelW + 12, 0);
  ctx.fillStyle = "#000";
  ctx.strokeStyle = "#E63946"; ctx.lineWidth = 3;
  ctx.fillRect(0, 0, 80, panelH);
  ctx.strokeRect(0, 0, 80, panelH);
  ctx.fillStyle = "#E63946";
  ctx.font = "bold 12px 'Press Start 2P', monospace";
  ctx.fillText("POS", 40, 18);
  ctx.fillStyle = "#FFF";
  ctx.font = "bold 22px 'Press Start 2P', monospace";
  ctx.fillText(`${pos}ST`, 40, 44);
  ctx.restore();
}

function drawDashHP(ctx, x, y, w, h, pct01) {
  ctx.fillStyle = "#0a0d14";
  ctx.fillRect(x, y, w, h);
  ctx.strokeStyle = OUTLINE; ctx.lineWidth = 2;
  ctx.strokeRect(x, y, w, h);
  const segs = 20;
  const segW = (w - 4) / segs;
  for (let i = 0; i < segs; i++) {
    if (i / segs > pct01) break;
    ctx.fillStyle = pct01 > 0.6 ? "#4AE863" : pct01 > 0.25 ? "#FFCC00" : "#E63946";
    ctx.fillRect(x + 2 + i * segW, y + 2, segW - 1, h - 4);
  }
  ctx.fillStyle = "#FFF";
  ctx.font = "bold 9px 'Press Start 2P', monospace";
  ctx.fillText("HP", x, y - 4);
}

function drawGauge(ctx, cx, cy, r, value01, label, color) {
  ctx.save();
  ctx.strokeStyle = OUTLINE;
  ctx.lineWidth = 4;
  ctx.fillStyle = "#FFFFFF";
  ctx.beginPath();
  ctx.arc(cx, cy, r, 0, Math.PI * 2);
  ctx.fill();
  ctx.stroke();
  // face ticks
  ctx.lineWidth = 2;
  ctx.strokeStyle = OUTLINE;
  for (let i = 0; i <= 10; i++) {
    const a = Math.PI * 0.75 + (i / 10) * Math.PI * 1.5;
    ctx.beginPath();
    ctx.moveTo(cx + Math.cos(a) * (r - 4), cy + Math.sin(a) * (r - 4));
    ctx.lineTo(cx + Math.cos(a) * (r - 10), cy + Math.sin(a) * (r - 10));
    ctx.stroke();
  }
  // needle
  const v = Math.max(0, Math.min(1, value01));
  const na = Math.PI * 0.75 + v * Math.PI * 1.5;
  ctx.strokeStyle = color;
  ctx.lineWidth = 5;
  ctx.lineCap = "round";
  ctx.beginPath();
  ctx.moveTo(cx, cy);
  ctx.lineTo(cx + Math.cos(na) * (r - 10), cy + Math.sin(na) * (r - 10));
  ctx.stroke();
  ctx.strokeStyle = OUTLINE;
  ctx.lineWidth = 3;
  ctx.beginPath();
  ctx.moveTo(cx, cy);
  ctx.lineTo(cx + Math.cos(na) * (r - 10), cy + Math.sin(na) * (r - 10));
  ctx.stroke();
  // center knob
  ctx.fillStyle = OUTLINE;
  ctx.beginPath();
  ctx.arc(cx, cy, 5, 0, Math.PI * 2);
  ctx.fill();
  // label
  ctx.fillStyle = color;
  ctx.font = "bold 14px 'Anton'";
  ctx.textAlign = "center";
  ctx.fillText(label, cx, cy + r + 18);
  ctx.restore();
}

function drawMiniMap(ctx, st, cx, cy, w, h) {
  ctx.save();
  ctx.fillStyle = "#2a2f3e";
  ctx.strokeStyle = OUTLINE;
  ctx.lineWidth = 3;
  ctx.beginPath();
  roundRect(ctx, cx - w / 2, cy - h / 2, w, h, 6);
  ctx.fill();
  ctx.stroke();
  // scale
  const sx = (x) => cx - w / 2 + 6 + ((x / 1280) * (w - 12));
  const sy = (y) => cy - h / 2 + 6 + ((y / 820) * (h - 12));
  // items (uncollected)
  for (const it of st.items) {
    if (it.collected) continue;
    ctx.fillStyle = "#FFD166";
    ctx.beginPath();
    ctx.arc(sx(it.x), sy(it.y), 3, 0, Math.PI * 2);
    ctx.fill();
  }
  // enemies
  for (const e of st.enemies) {
    ctx.fillStyle = e.type === "fbi" ? "#FFD700" : "#E63946";
    ctx.fillRect(sx(e.x) - 2, sy(e.y) - 2, 4, 4);
  }
  // player
  ctx.fillStyle = "#4AE863";
  const px = sx(st.player.x), py = sy(st.player.y);
  ctx.beginPath();
  ctx.arc(px, py, 3.5, 0, Math.PI * 2);
  ctx.fill();
  // player facing line
  ctx.strokeStyle = "#4AE863";
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(px, py);
  ctx.lineTo(px + Math.cos(st.player.angle) * 6, py + Math.sin(st.player.angle) * 6);
  ctx.stroke();
  // label
  ctx.fillStyle = "#FFD166";
  ctx.font = "bold 9px 'JetBrains Mono'";
  ctx.textAlign = "center";
  ctx.fillText("REARVIEW", cx, cy - h / 2 - 2);
  ctx.restore();
}

function drawSteeringWheel(ctx, st) {
  ctx.save();
  const cx = WORLD_W / 2;
  const cy = WORLD_H - 20;
  const r = 130;
  // steering angle: use left/right key input for cartoon effect
  const turn = (st.keys["arrowleft"] || st.keys["a"]) ? -0.4 :
               (st.keys["arrowright"] || st.keys["d"]) ? 0.4 : 0;
  ctx.translate(cx, cy);
  ctx.rotate(turn);

  // outer ring
  ctx.fillStyle = "#1a1a1a";
  ctx.strokeStyle = OUTLINE;
  ctx.lineWidth = 5;
  ctx.beginPath();
  ctx.arc(0, 0, r, 0, Math.PI * 2);
  ctx.fill();
  ctx.stroke();
  // inner rim
  ctx.fillStyle = "#3a3a3a";
  ctx.beginPath();
  ctx.arc(0, 0, r - 18, 0, Math.PI * 2);
  ctx.fill();
  ctx.stroke();
  // center hub
  ctx.fillStyle = "#FF3B30";
  ctx.beginPath();
  ctx.arc(0, 0, 32, 0, Math.PI * 2);
  ctx.fill();
  ctx.stroke();
  ctx.fillStyle = "#FFD166";
  ctx.font = "bold 16px 'Anton'";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText("GP", 0, 0);
  // spokes
  ctx.strokeStyle = OUTLINE;
  ctx.lineWidth = 6;
  for (const a of [0, Math.PI * 0.6, Math.PI * 1.4]) {
    ctx.beginPath();
    ctx.moveTo(Math.cos(a) * 28, Math.sin(a) * 28);
    ctx.lineTo(Math.cos(a) * (r - 22), Math.sin(a) * (r - 22));
    ctx.stroke();
  }
  ctx.restore();

  // hands on the wheel (don't rotate with wheel)
  ctx.save();
  ctx.fillStyle = "#F1C27D";
  ctx.strokeStyle = OUTLINE;
  ctx.lineWidth = 3;
  // left hand
  ctx.beginPath();
  ctx.ellipse(cx - 110, cy - 38, 22, 16, -0.2, 0, Math.PI * 2);
  ctx.fill(); ctx.stroke();
  // right hand
  ctx.beginPath();
  ctx.ellipse(cx + 110, cy - 38, 22, 16, 0.2, 0, Math.PI * 2);
  ctx.fill(); ctx.stroke();
  ctx.restore();
}

/**
 * Windshield frame — the dark "you're inside the car" border that
 * surrounds the projected world view. Three layers stacked:
 *   1. Soft radial vignette (interior shadow)
 *   2. Headliner — chunky black band along the very top
 *   3. A-pillars — angled black bars on the left + right that meet the
 *      headliner at ~25 degrees, mimicking the windshield post seen in
 *      real driving views (Project Cars / GT Sport reference photos).
 * The dashboard chrome already covers the bottom — no need to repeat.
 */
function drawWindshieldFrame(ctx) {
  // 1. Vignette — gradient from transparent center to black at edges.
  const vg = ctx.createRadialGradient(
    WORLD_W / 2, WORLD_H * 0.55, WORLD_W * 0.18,
    WORLD_W / 2, WORLD_H * 0.55, WORLD_W * 0.62,
  );
  vg.addColorStop(0, "rgba(0,0,0,0)");
  vg.addColorStop(0.7, "rgba(0,0,0,0.18)");
  vg.addColorStop(1, "rgba(0,0,0,0.55)");
  ctx.fillStyle = vg;
  ctx.fillRect(0, 0, WORLD_W, WORLD_H);

  // 2. Headliner — sleek black band at the top with subtle pinstripe.
  const headlinerH = 56;
  ctx.fillStyle = "#0E0C12";
  ctx.fillRect(0, 0, WORLD_W, headlinerH);
  ctx.fillStyle = "#1F1B26";
  ctx.fillRect(0, headlinerH - 6, WORLD_W, 6);
  // Glossy highlight strip
  ctx.fillStyle = "rgba(255,255,255,0.06)";
  ctx.fillRect(0, headlinerH - 12, WORLD_W, 2);

  // 3. A-pillars — chunky angled bars on each side, running from the
  //    headliner all the way down to the dashboard chrome (~y=720).
  //    Wider at the top, slimmer at the bottom so they read like real
  //    car windshield posts.
  const pillarTopW = 110;
  const pillarBotW = 60;
  const pillarBotY = WORLD_H * 0.88;  // meets the dashboard
  ctx.fillStyle = "#13111A";
  ctx.strokeStyle = "#000";
  ctx.lineWidth = 2;
  // Left A-pillar
  ctx.beginPath();
  ctx.moveTo(0, headlinerH);
  ctx.lineTo(pillarTopW, headlinerH);
  ctx.lineTo(pillarBotW, pillarBotY);
  ctx.lineTo(0, pillarBotY);
  ctx.closePath();
  ctx.fill();
  ctx.stroke();
  // Right A-pillar
  ctx.beginPath();
  ctx.moveTo(WORLD_W, headlinerH);
  ctx.lineTo(WORLD_W - pillarTopW, headlinerH);
  ctx.lineTo(WORLD_W - pillarBotW, pillarBotY);
  ctx.lineTo(WORLD_W, pillarBotY);
  ctx.closePath();
  ctx.fill();
  ctx.stroke();
  // A-pillar trim — thin gold accent strip running down each post
  ctx.strokeStyle = "rgba(224,177,0,0.7)";
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(pillarTopW - 4, headlinerH + 4);
  ctx.lineTo(pillarBotW - 4, pillarBotY - 4);
  ctx.stroke();
  ctx.beginPath();
  ctx.moveTo(WORLD_W - pillarTopW + 4, headlinerH + 4);
  ctx.lineTo(WORLD_W - pillarBotW + 4, pillarBotY - 4);
  ctx.stroke();
}
